'use client'

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";

import { useAppParams } from "@/common/hooks/useAppParams";
import { useUpdateQueryParams } from "@/widgets/BecomeAMember/hooks/useUpdateQueryParams";
import { useExtensionFlow } from "@/widgets/ExtensionFlow/context/ExtensionFlowContext";

import { ApiError } from "@/common/api/api-client";
import { Maybe } from "@/generated/contentfulTypes";

import { MergedClub } from "@/common/types/api.interfaces";
import { DEFAULT_MEMBER_VERIFICATION_VALUES } from "./MemberVerification.utils";
import { ExtensionVerificationForm, ExtensionVerificationFormTypes } from "@/common/utils/formFields";
import { getCustomerBy } from "@/common/utils/getCustomerBy";
import { setCookie } from "@/common/utils/cookies";
import { CookieKeys, UrlParamKeys } from "@/common/utils/constants";
import { getCountryCodeFromLocale } from "@/common/utils/getCountryCodeFromLocale";

export const useMemberVerification = ({ preselectedClub, onVerificationSuccess }: { preselectedClub: Maybe<MergedClub> | undefined, onVerificationSuccess: () => void }) => {
    const { lang } = useAppParams();
    const { updateUrl, searchParams } = useUpdateQueryParams();
    const router = useRouter();

    const { pushToDataLayer, isAuthorized } = useExtensionFlow();

    const trackedClubIdRef = useRef<string | null>(null);
    const trackedMemberVerificationRef = useRef(false);

    const pushSelectAClubAnalytics = useCallback((club: {
        clubId: string;
        studioName: string;
        city: string;
    }) => {
        if (!club.clubId || trackedClubIdRef.current === club.clubId) {
            return;
        }

        trackedClubIdRef.current = club.clubId;
        pushToDataLayer({
            funnel_name: 'extension',
            funnel_step_name: 'select_a_club',
            funnel_step: '2',
            city: club.city,
            studio_name: club.studioName,
            studio_id: club.clubId,
        });
    }, [pushToDataLayer]);

    useEffect(() => {
        if (isAuthorized === false && !trackedMemberVerificationRef.current) {
            trackedMemberVerificationRef.current = true;
            pushToDataLayer({
                funnel_step_name: 'member_verification',
                funnel_step: '1',
            });
        }
    }, [isAuthorized]);

    const countryCode = getCountryCodeFromLocale(lang);
    const storageKey = `member-verification-form-storage`;

    const membershipNumber = searchParams.get(UrlParamKeys.MembershipNumber);

    const defaultHomeClub = useMemo(() => {
        if (preselectedClub?.clubId && preselectedClub.address.countryCodeAlpha2 === countryCode) {
            return {
                studioName: preselectedClub.studioName,
                clubId: preselectedClub.clubId,
                city: preselectedClub.address.city,
                countryCode: preselectedClub.address.countryCodeAlpha2,
            };
        }

        try {
            const storedValues = sessionStorage.getItem(storageKey);
            if (storedValues) {
                const parsed = JSON.parse(storedValues);
                if (parsed?.homeClub.countryCode === countryCode) {
                    return parsed.homeClub;
                } else {
                    sessionStorage.removeItem(storageKey)
                }
            }
        } catch (error) {

        }

        return null;
    }, [preselectedClub, storageKey]);

    useEffect(() => {
        if (!isAuthorized && (defaultHomeClub?.clubId)) {
            pushSelectAClubAnalytics({
                clubId: defaultHomeClub.clubId,
                studioName: defaultHomeClub.studioName,
                city: defaultHomeClub.city,
            });
        }
    }, [isAuthorized, defaultHomeClub]);


    const initialFormValues = useMemo(() => {
        try {
            const storedValues = sessionStorage.getItem(storageKey);
            if (storedValues) {
                const parsed = JSON.parse(storedValues);
                return {
                    ...DEFAULT_MEMBER_VERIFICATION_VALUES,
                    ...parsed,
                    [ExtensionVerificationForm.HomeClub]: defaultHomeClub ?? parsed[ExtensionVerificationForm.HomeClub] ?? null,
                    [ExtensionVerificationForm.MembershipNumber]: membershipNumber ?? parsed[ExtensionVerificationForm.MembershipNumber] ?? null,
                };
            }
        } catch (error) {
            console.warn('Failed to parse session storage values:', error);
        }

        return {
            ...DEFAULT_MEMBER_VERIFICATION_VALUES,
            [ExtensionVerificationForm.HomeClub]: defaultHomeClub ?? null,
            [ExtensionVerificationForm.MembershipNumber]: membershipNumber ?? null,
        };
    }, [storageKey, defaultHomeClub]);

    const { handleSubmit, control, trigger, setValue, getValues, } =
        useForm<ExtensionVerificationFormTypes>({
            defaultValues: initialFormValues,
            mode: 'onBlur',
            reValidateMode: 'onChange',
        });

    useEffect(() => {
        const currentHomeClub = getValues(ExtensionVerificationForm.HomeClub);

        if (defaultHomeClub) {
            if (currentHomeClub?.clubId !== defaultHomeClub.clubId) {
                setValue(ExtensionVerificationForm.HomeClub, defaultHomeClub, { shouldValidate: false });
            }
        } else if (currentHomeClub !== null && currentHomeClub !== undefined) {
            const storedValues = sessionStorage.getItem(storageKey);
            const storedHomeClub = storedValues ? JSON.parse(storedValues)?.[ExtensionVerificationForm.HomeClub] : null;
            if (!storedHomeClub) {
                setValue(ExtensionVerificationForm.HomeClub, null, { shouldValidate: false });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultHomeClub]);

    const saveFormData = useCallback(() => {
        sessionStorage.setItem(storageKey, JSON.stringify(getValues()));
    }, [storageKey, getValues]);

    const handleBlur = useCallback(() => {
        saveFormData();
    }, [saveFormData]);

    const onSubmit = async (
        data: ExtensionVerificationFormTypes,
    ) => {
        try {
            const customer = await getCustomerBy({
                customerNumber: data.membershipNumber,
                studioId: data.homeClub?.clubId as string,
                dateOfBirth: data.dateOfBirth,
                lang: lang,
            });

            if (customer) {
                if (data.homeClub && customer.end_date) {
                    setCookie(CookieKeys.EXTENSION_END_DATE, customer.end_date);
                    setCookie(CookieKeys.EXTENSION_HAS_PAYMENT_METHOD, customer.has_payment_method ? 'true' : 'false');
                    setCookie(CookieKeys.EXTENSION_CUSTOMER_ID, customer.customer_id);

                    // Use push instead of replace to preserve the previous URL in history
                    const params = new URLSearchParams(searchParams.toString());
                    params.set(UrlParamKeys.StudioId, data.homeClub.clubId);
                    router.push(`${window.location.pathname}?${params.toString()}`, {
                        scroll: false,
                    });
                }

                if (customer.jwt_token) {
                    setCookie(CookieKeys.EXTENSION_AUTH_TOKEN, customer.jwt_token);
                }

                onVerificationSuccess();
            }
        } catch (error) {
            if (error instanceof ApiError && error.data?.errorId) {
                updateUrl({ error: error.data.errorId });
            }
            console.error(error);
        }
    };

    const handleContinueWithCurrentClub = () => {
        trigger().then((isValid) => {
            if (isValid) {
                handleSubmit((data) =>
                    onSubmit(data)
                )();
            }
        });
    };

    return { handleBlur, handleContinueWithCurrentClub, control, trigger, pushSelectAClubAnalytics };
}